/**
 * The account and language dialog of the settings screen (port addition). The settings screen of the original had
 * no room for the port's controls: the language list, "sign out" and "delete account" were squeezed into the bottom
 * left corner of its left panel (rows 11 CSS px tall on a phone, a mis-tap between two languages restarting the
 * game). The screen now has one full-size button of its own ("Account and language", beside the panels' bottom
 * buttons), which opens this dialog over it: the frame of the game's popups (menu_tired's, as the sign-in screen and
 * the delete-account dialog) and in it, each a full-size button of the game's popups apart from the others:
 *  - the account (its nickname: data, never translated; a guest's default name is game text);
 *  - the languages side by side (the active one marked, not a target): a pick stores it and restarts the game;
 *  - sign out (an account with a password; a guest of an earlier version has none to come back with) and delete the
 *    account (the confirmation dialog: src/game/ui/delete-account.ts);
 *  - where the Android app offers them: linking Google Play Games, the ad privacy options;
 *  - back (also Escape and the Android back button).
 * On a wide screen the dialog is shown larger and the settings screen under it is hidden (ui/port-dialog.ts).
 */
import { Event, KeyboardEvent, Shape, Sprite } from '@flash';
import * as T from '../runtime/auth-texts';
import { LANGUAGES, language, playerName, translate, type Language } from '../runtime/i18n';
import { BigButton, SmallButton, label, popupFrame, setRawText } from './controls';
import { fitDialogBody, hideScreenUnder } from './port-dialog';

export interface AccountDialogOptions {
  /** A language was picked (not the active one): the game stores it and restarts. */
  onLanguage(lang: Language): void;
  /** Sign out (the dialog is closed first). */
  onSignOut(): void;
  /** Delete the account: its confirmation dialog (the dialog is closed first). */
  onDelete(): void;
  /** Extra actions of the app (Play Games, the ad privacy options): their names, labels and what they do; none on the web. */
  extras?: Array<{ name: string; label: string; run(): void }>;
  /** The dialog was closed. */
  onClosed(): void;
}

/** The frame (menu_tired's at 0.8, 344 x 389) in the middle of the 807 x 600 stage, and its content column. */
const PANEL_SCALE = 0.8;
const FRAME_W = 344;
const FRAME_H = 389;
const FRAME_X = Math.round((807 - FRAME_W) / 2);
const FRAME_Y = Math.round((600 - FRAME_H) / 2);
const COL_X = 26;
const COL_W = 270;
/** The popups' buttons at 0.9 (130 x 36): two side by side in the column, 10 px apart. */
const BUTTON_SCALE = 0.9;
const GAP = 10;
const DIM = { x: -2000, y: -1000, w: 4807, h: 2600, alpha: 0.5 };
const TITLE_COLOR = 0x333333;

/** The title and the label of the language buttons (the source strings; the catalogue translates them). */
export const MSG_ACCOUNT_TITLE = 'Аккаунт и язык';
export const MSG_ACCOUNT_BUTTON = 'Аккаунт и язык';
export const MSG_LANGUAGE = 'Язык';
export const MSG_SIGN_OUT_FULL = 'Выйти из аккаунта';
export const MSG_DELETE_FULL = 'Удалить аккаунт';
export const MSG_BACK = 'назад';
export const ACCOUNT_TEXTS = [MSG_ACCOUNT_TITLE, MSG_ACCOUNT_BUTTON, MSG_LANGUAGE, MSG_SIGN_OUT_FULL, MSG_DELETE_FULL, MSG_BACK];

export class AccountDialog {
  /** Everything of the dialog (added to the game's system layer by FWP.net_account_dialog). */
  readonly root = new Sprite();
  /** The language buttons (by language), sign out (an account only), delete, back. */
  readonly languages = new Map<Language, BigButton>();
  readonly signOutButton: BigButton | null;
  readonly deleteButton: BigButton;
  readonly backButton: SmallButton;
  private readonly body = new Sprite();
  private readonly restoreUnder: () => void;
  private disposed = false;
  private stageRef: any = null;

  constructor(
    private readonly fwp: any,
    private readonly opts: AccountDialogOptions,
  ) {
    const user = fwp.user || {};
    this.root.name = '$account_dialog';
    const dim = new Sprite();
    dim.name = '$account_dialog_dim';
    dim.graphics.beginFill(0x000000, DIM.alpha);
    dim.graphics.drawRect(DIM.x, DIM.y, DIM.w, DIM.h);
    dim.graphics.endFill();
    this.root.addChild(dim);
    this.body.name = '$account_dialog_body';
    this.root.addChild(this.body);
    const panel = popupFrame(fwp, FRAME_X, FRAME_Y, PANEL_SCALE);
    panel.name = '$account_dialog_panel';
    this.body.addChild(panel);
    const page = new Sprite();
    page.name = '$account_dialog_content';
    page.x = FRAME_X;
    page.y = FRAME_Y;
    this.body.addChild(page);

    // The parts from the top, then placed with the frame's free height shared between the gaps (no empty band under
    // "back").
    const title = label(MSG_ACCOUNT_TITLE, { size: 14, bold: true, color: TITLE_COLOR, align: 'center', width: COL_W });
    title.name = 'account_title';
    const account = label('', { size: 12, align: 'center', width: COL_W });
    account.name = 'account_nickname';
    // The nickname is the player's own and stays as typed; a guest's default name is game text.
    const name = String(user.name ?? '');
    setRawText(account, `${translate(T.MSG_DELETE_ACCOUNT_OF)} ${user.guest ? playerName(name) : name}`);
    const lang = label(MSG_LANGUAGE, { size: 12, bold: true });
    lang.name = 'account_language_label';
    // The languages two by two: the active one green with a check mark (not a target), the others the neutral beige.
    const active = language();
    const buttons: BigButton[] = [];
    LANGUAGES.forEach(({ id, label: text }) => {
      const b = new BigButton(fwp, id, text, id === active ? 'green' : 'beige');
      // (a language's name is its own, never translated; the active one marked)
      setRawText(b.caption, (id === active ? '✓ ' : '') + text);
      b.scaleX = b.scaleY = BUTTON_SCALE;
      if (id === active) {
        b.mouseEnabled = false;
        b.mouseChildren = false;
      } else b.addEventListener('click', () => this.pickLanguage(id));
      this.languages.set(id, b);
      buttons.push(b);
    });
    const line = new Shape();
    line.name = 'account_separator';
    // Signing out needs a password to come back with: a guest of an earlier version has none.
    const account2: BigButton[] = [];
    this.signOutButton = null;
    if (!user.guest && user.player_id) {
      this.signOutButton = new BigButton(fwp, 'account_sign_out', MSG_SIGN_OUT_FULL, 'green');
      this.signOutButton.addEventListener('click', () => this.run(() => this.opts.onSignOut()));
      account2.push(this.signOutButton);
    }
    this.deleteButton = new BigButton(fwp, 'account_delete', MSG_DELETE_FULL, 'beige');
    this.deleteButton.caption.textColor = 0xb00000;
    this.deleteButton.addEventListener('click', () => this.run(() => this.opts.onDelete()));
    account2.push(this.deleteButton);
    for (const b of account2) b.scaleX = b.scaleY = BUTTON_SCALE;
    // The app's extras (small buttons) and back.
    const extras = (opts.extras ?? []).map((e) => {
      const b = new SmallButton(fwp, e.name, e.label);
      b.addEventListener('click', () => this.run(() => e.run()));
      return b;
    });
    this.backButton = new SmallButton(fwp, 'account_back', MSG_BACK);
    this.backButton.addEventListener('click', () => this.cancel());
    // The heights of the parts and the gaps before them (as the dialog was drawn), the free height shared out.
    const rowsH = (list: Array<BigButton | SmallButton>) => {
      const rowH = Math.max(...list.map((b) => b.height));
      return Math.ceil(list.length / 2) * (rowH + GAP) - GAP;
    };
    const parts: Array<{ h: number; gap: number; place: (y: number) => void }> = [
      { h: 24, gap: 0, place: (y) => place(page, title, COL_X, y) },
      { h: 20, gap: 0, place: (y) => place(page, account, COL_X, y) },
      { h: 20, gap: 8, place: (y) => place(page, lang, COL_X, y) },
      { h: rowsH(buttons), gap: 0, place: (y) => this.rows(page, buttons, y) },
      {
        h: 0,
        gap: 8,
        place: (y) => {
          line.graphics.lineStyle(1, 0x9aa870, 1);
          line.graphics.moveTo(COL_X + 8, y);
          line.graphics.lineTo(COL_X + COL_W - 8, y);
          page.addChild(line);
        },
      },
      { h: rowsH(account2), gap: 10, place: (y) => this.rows(page, account2, y) },
      ...(extras.length ? [{ h: rowsH(extras), gap: 4, place: (y: number) => this.rows(page, extras, y) }] : []),
      { h: this.backButton.height, gap: 12, place: (y) => place(page, this.backButton, COL_X + (COL_W - this.backButton.width) / 2, y) },
    ];
    const first = 18;
    const last = FRAME_H - 40;
    const used = parts.reduce((a, p) => a + p.h + p.gap, 0);
    // (the room left over goes between the title block and the languages, round the line between the languages and
    // the account's actions, and between them and back)
    const share: Record<number, number> = { 2: 1, 4: 0.5, 5: 0.5, [parts.length - 1]: 1 };
    const extra = Math.max(0, last - first - used) / 3;
    let y = first;
    let prevBottom = first;
    parts.forEach((p, i) => {
      y += p.gap + (share[i] ?? 0) * extra;
      if (i === parts.length - 1) {
        // Back's target: from the last row to the frame's content bottom, across the column.
        const by = Math.round(y);
        p.place(by);
        this.backButton.setHitRect({ x: COL_X - this.backButton.x, y: prevBottom + 2 - by, width: COL_W, height: FRAME_H - 30 - (prevBottom + 2) });
      } else p.place(Math.round(y));
      y += p.h;
      prevBottom = Math.round(y);
    });
    this.root.addEventListener(Event.ENTER_FRAME, this.onFrame);
    this.restoreUnder = hideScreenUnder(fwp);
  }

  /**
   * Buttons two by two from `y` (each row centred in the column), each a finger's target of its row's share: the
   * room between the rows and the buttons split half way. Returns the y under the last row.
   */
  private rows(page: Sprite, list: Array<BigButton | SmallButton>, y: number): number {
    const rowH = Math.max(...list.map((b) => b.height));
    for (let i = 0; i < list.length; i += 2) {
      const row = list.slice(i, i + 2);
      const w = row.reduce((a, b) => a + b.width, 0) + GAP * (row.length - 1);
      let x = COL_X + (COL_W - w) / 2;
      for (const b of row) {
        place(page, b, x, y + (rowH - b.height) / 2);
        // The target: the button with half the gap round it (its own coordinates: unscaled).
        const k = b.scaleX;
        b.setHitRect({ x: -GAP / 2 / k, y: -(GAP / 2 + (rowH - b.height) / 2) / k, width: (b.width + GAP) / k, height: (rowH + GAP) / k });
        x += b.width + GAP;
      }
      y += rowH + GAP;
    }
    return y - GAP;
  }

  /** Starts listening to the keyboard (once the dialog is on the stage). */
  attach(): void {
    const st = this.root.stage;
    if (!st || this.stageRef) return;
    this.stageRef = st;
    st.addEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
  }

  /**
   * The dialog's frame in stage coordinates, or null when it is not shown or a wide screen shows it larger itself: a
   * small screen without the wide view zooms into it (the browser host's focusRect, src/main.ts).
   */
  focusRect(): { x: number; y: number; width: number; height: number } | null {
    // (a wide host shows it larger itself: ui/port-dialog.ts)
    if (this.disposed || !this.root.stage || (this.root.stage as any).$visibleArea) return null;
    return { x: FRAME_X, y: FRAME_Y, width: FRAME_W, height: FRAME_H };
  }

  /** Back (the Android back button): closes the dialog. Always handled while open. */
  back(): boolean {
    if (!this.disposed) this.cancel();
    return true;
  }

  /** Closes the dialog. */
  cancel(): void {
    if (this.disposed) return;
    this.dispose();
    this.opts.onClosed();
  }

  /** Removes the dialog and its listeners; the settings screen under it is shown again. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.stageRef) this.stageRef.removeEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
    this.stageRef = null;
    this.root.removeEventListener(Event.ENTER_FRAME, this.onFrame);
    if (this.root.parent) this.root.parent.removeChild(this.root);
    this.restoreUnder();
  }

  private pickLanguage(id: Language): void {
    if (this.disposed || id === language()) return;
    this.opts.onLanguage(id);
  }

  /** An action that leaves the dialog: closed first (the next dialog, the restart). */
  private run(action: () => void): void {
    if (this.disposed) return;
    this.dispose();
    this.opts.onClosed();
    action();
  }

  private readonly onKey = (e: any): void => {
    if (!this.disposed && e.keyCode === 27) this.cancel();
  };

  private readonly onFrame = (): void => {
    fitDialogBody(this.body, { x: FRAME_X, y: FRAME_Y, width: FRAME_W, height: FRAME_H }, this.root.stage);
  };
}

function place(parent: Sprite, child: any, x: number, y: number): void {
  child.x = Math.round(x);
  child.y = Math.round(y);
  parent.addChild(child);
}
